/**
 * Precision Animated SVG Arrow with straight/bezier curved paths and marker heads.
 */
import { SVGObject, SVGObjectConfig } from './SVGObject';
import { Renderer } from '../core/Renderer';

export interface ArrowProps {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  cx?: number; // Bezier control point X
  cy?: number; // Bezier control point Y
  color?: string;
  strokeWidth?: number;
  dashed?: boolean;
  marker?: string; // 'arrow-blue' | 'arrow-gold' | 'arrow-green' | 'arrow-red' | 'arrow-default'
  drawProgress?: number; // 0 to 1
}

export class Arrow extends SVGObject {
  public props: ArrowProps;
  private pathEl: SVGPathElement | null = null;

  constructor(config: SVGObjectConfig & { props?: ArrowProps }) {
    super({ ...config, type: 'arrow' });
    this.props = {
      x1: 0,
      y1: 0,
      x2: 100,
      y2: 0,
      color: '#38bdf8',
      strokeWidth: 3,
      marker: 'arrow-blue',
      drawProgress: 1,
      ...config.props
    };
  }

  public render(): void {
    this.container.innerHTML = '';
    const path = Renderer.createElement('path');

    let d = `M ${this.props.x1} ${this.props.y1}`;
    if (this.props.cx !== undefined && this.props.cy !== undefined) {
      d += ` Q ${this.props.cx} ${this.props.cy} ${this.props.x2} ${this.props.y2}`;
    } else {
      d += ` L ${this.props.x2} ${this.props.y2}`;
    }

    Renderer.setAttributes(path, {
      d,
      fill: 'none',
      stroke: this.props.color || '#38bdf8',
      'stroke-width': this.props.strokeWidth || 3,
      'stroke-linecap': 'round',
      'marker-end': this.props.marker ? `url(#${this.props.marker})` : 'url(#arrow-blue)',
      'stroke-dasharray': this.props.dashed ? '6,6' : null
    });

    this.pathEl = path;
    this.container.appendChild(path);
    this.updateDrawProgress();
  }

  public setDrawProgress(progress: number): void {
    this.props.drawProgress = Math.max(0, Math.min(1, progress));
    this.updateDrawProgress();
  }

  private updateDrawProgress(): void {
    if (!this.pathEl) return;
    try {
      const len = this.pathEl.getTotalLength();
      if (len > 0) {
        const offset = len * (1 - (this.props.drawProgress ?? 1));
        this.pathEl.style.strokeDasharray = `${len}`;
        this.pathEl.style.strokeDashoffset = `${offset}`;
      }
    } catch {
      // Ignore if not in DOM yet
    }
  }

  public setCoordinates(x1: number, y1: number, x2: number, y2: number, cx?: number, cy?: number): void {
    this.props.x1 = x1;
    this.props.y1 = y1;
    this.props.x2 = x2;
    this.props.y2 = y2;
    this.props.cx = cx;
    this.props.cy = cy;
    this.render();
  }
}
